import type { LucideIcon } from "lucide-react";

import { LanguageSwitcher, Logo, ThemeToggle } from "@/components/shared";
import { cn } from "@/lib/utils";

type Tone = "brand" | "danger" | "warning";

const BADGE: Record<Tone, string> = {
  brand: "bg-primary/10 text-primary ring-primary/15",
  danger: "bg-destructive/10 text-destructive ring-destructive/15",
  warning: "bg-warning/10 text-warning ring-warning/20",
};

const ACCENT: Record<Tone, string> = {
  brand: "from-caramel-400 via-coffee-500 to-coffee-900",
  danger: "from-caramel-400 via-destructive to-destructive",
  warning: "from-caramel-400 via-warning to-caramel-600",
};

export type AuthNoticeProps = {
  icon: LucideIcon;
  title: string;
  body?: string;
  /** Nima qilish kerakligi — bir jumlalik izohdan ko'ra aniqroq. */
  steps?: string[];
  action?: React.ReactNode;
  footnote?: string;
  tone?: Tone;
};

/**
 * Panelga kirishdan oldingi yagona ekran: kirish talab qilinganda,
 * ruxsat yo'qligida va tarmoq yiqilganda ham shu ko'rinish ishlaydi —
 * faqat ikonka, ohang va matn almashadi.
 *
 * Bu sahifa ko'pincha odam panelni BIRINCHI marta ochganda ko'rinadi,
 * shuning uchun u bo'sh xabar emas, brendlangan darvoza: logotip,
 * qadamlar va til/mavzu boshqaruvi shu yerda turadi (yon panel hali yo'q).
 */
export function AuthNotice({
  icon: Icon,
  title,
  body,
  steps,
  action,
  footnote,
  tone = "brand",
}: AuthNoticeProps) {
  return (
    <div className="bg-background relative flex min-h-dvh flex-col overflow-hidden">
      {/* Bezak qatlami — o'lchamdan tashqarida turadi va bosishni ushlamaydi. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="bg-coffee-300/25 dark:bg-coffee-700/20 absolute -top-40 -left-32 size-[28rem] rounded-full blur-3xl" />
        <div className="bg-caramel-400/20 dark:bg-caramel-600/10 absolute -right-32 -bottom-40 size-[26rem] rounded-full blur-3xl" />
        <div className="absolute inset-0 [background-image:radial-gradient(currentColor_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_at_center,black,transparent_72%)] [background-size:22px_22px] opacity-[0.05]" />
      </div>

      <header className="relative flex items-center justify-between gap-4 px-5 py-5 sm:px-8">
        <Logo tone="page" />
        <div className="flex items-center gap-1.5">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </header>

      <main className="relative flex flex-1 items-center justify-center px-5 pb-16 sm:px-8">
        <div className="animate-in fade-in slide-in-from-bottom-3 w-full max-w-md duration-500">
          <div className="bg-card/80 ring-foreground/10 overflow-hidden rounded-2xl shadow-lg ring-1 backdrop-blur-sm">
            <div className={cn("h-1 w-full bg-gradient-to-r", ACCENT[tone])} />

            <div className="px-7 py-8 sm:px-8">
              <span
                className={cn(
                  "mb-6 grid size-14 place-items-center rounded-2xl ring-1",
                  BADGE[tone],
                )}
              >
                <Icon className="size-6" />
              </span>

              <h1 className="text-foreground text-2xl font-semibold tracking-tight text-balance">
                {title}
              </h1>
              {body && (
                <p className="text-muted-foreground mt-2.5 text-sm leading-relaxed text-pretty">
                  {body}
                </p>
              )}

              {steps && steps.length > 0 && (
                <ol className="border-border/60 mt-6 space-y-3 border-t pt-6">
                  {steps.map((step, index) => (
                    <li key={step} className="flex items-start gap-3">
                      <span className="bg-muted text-muted-foreground mt-px grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold tabular-nums">
                        {index + 1}
                      </span>
                      <span className="text-foreground/85 text-sm leading-6">{step}</span>
                    </li>
                  ))}
                </ol>
              )}

              {action && <div className="mt-7 [&>*]:w-full">{action}</div>}

              {footnote && (
                <p className="text-muted-foreground/80 mt-5 text-center text-xs leading-relaxed">
                  {footnote}
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
